import { useMemo, useState } from 'react'
import { PageHeader, StatTile, Panel, DataTable, SegmentedControl, Banner, Chip, BarChart, Field, type Column } from '../ds/index'
import { useApi } from '../lib/useApi'
import { num, dateShort } from '../lib/format'
import { HBars } from '../components/Viz'
import { LoadingBlock, ErrorBlock } from '../components/Loading'

type Unit = { source: 'measured' | 'estimated'; calls: number; micros: number; input_tokens: number; output_tokens: number; model?: string }
type Costs = {
  days: number; total_micros: number; real_calls: number; simulated_calls: number
  per_email_micros: number | null; per_post_micros: number | null; per_lead_micros: number | null; month_at_current_rate_micros: number
  volume: { emails: number; posts: number; screened: number; leads: number; groups: number; active_days: number }
  projection: { posts_per_group_per_day: number | null; emails_per_group_per_day: number | null; lead_share: number | null; lead_share_from_simulated: boolean; unit: { screen: Unit; extract: Unit } }
  prices: Record<string, { input: number; output: number } | null>
  by_model: { stage: string; model: string; calls: number; failed: number; input_tokens: string; output_tokens: string; cache_read_tokens: string; cost_micros: string; avg_latency_ms: number }[]
  by_day: { day: string; screen_micros: string; extract_micros: string; calls: number }[]
  by_group: { name: string; posts: number; leads: number; cost_micros: string }[]
}

/** Dollars from millionths of a dollar, with enough places that a single call is not $0.00. */
export const usd = (micros: number | string | null | undefined) => {
  if (micros == null) return 'n/a'
  const d = Number(micros) / 1e6
  if (d === 0) return '$0.00'
  if (Math.abs(d) < 0.01) return `$${d.toFixed(5)}`
  if (Math.abs(d) < 10) return `$${d.toFixed(3)}`
  return `$${d.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}
const modelName = (m: string) => m.replace(/-\d{8}$/, '')
const STEP: Record<string, string> = { screen: 'Screen every post', extract: 'Extract leads', shadow: 'Shadow screen (comparison)', grade: 'Grade conversations', match: 'Match clients', draft: 'Suggested replies' }

export default function CostsPage() {
  const [days, setDays] = useState('30')
  const { data, error } = useApi<Costs>(`/api/costs?days=${days}`)
  if (error) return <ErrorBlock error={error} />

  const cols: Column<Costs['by_model'][number]>[] = [
    { key: 'stage', header: 'Step', cell: (r) => <strong>{STEP[r.stage] || r.stage}</strong> },
    { key: 'model', header: 'Model', cell: (r) => <span className="num">{modelName(r.model)}</span> },
    { key: 'calls', header: 'Calls', numeric: true, cell: (r) => <>{num(r.calls)}{r.failed ? <span className="muted"> ({r.failed} failed)</span> : ''}</> },
    { key: 'in', header: 'Input tokens', numeric: true, cell: (r) => num(r.input_tokens) },
    { key: 'out', header: 'Output tokens', numeric: true, cell: (r) => num(r.output_tokens) },
    { key: 'lat', header: 'Avg time', numeric: true, cell: (r) => `${(r.avg_latency_ms / 1000).toFixed(1)} s` },
    { key: 'cost', header: 'Cost', numeric: true, cell: (r) => usd(r.cost_micros) },
  ]

  return (
    <>
      <PageHeader title="AI cost" subtitle="SPEC only. What each email, post and lead costs to process, and what a month costs at any number of groups."
                  actions={<SegmentedControl value={days} onChange={setDays} segments={[{ value: '7', label: '7 days' }, { value: '30', label: '30 days' }, { value: '90', label: '90 days' }]} />} />
      {!data ? <LoadingBlock /> : (
        <>
          {data.simulated_calls > 0 && data.real_calls === 0 && (
            <div style={{ marginBottom: 'var(--s-6)' }}>
              <Banner tone="warn" title="No real model calls in this window yet">
                {num(data.simulated_calls)} posts were screened by the demo's keyword rules, which cost nothing and are not priced. Until real calls arrive, unit costs below are estimates built from this install's actual prompts.
              </Banner>
            </div>
          )}
          <div className="grid-tiles" style={{ marginBottom: 'var(--s-6)' }}>
            <StatTile focal label="A month at this rate" value={data.real_calls ? usd(data.month_at_current_rate_micros) : 'n/a'} meta={data.real_calls ? `${usd(data.total_micros)} over ${data.volume.active_days} active days` : 'no real calls yet'} />
            <StatTile label="Per 1,000 emails" value={data.per_email_micros == null ? 'n/a' : usd(data.per_email_micros * 1000)} meta={`${num(data.volume.emails)} emails in`} />
            <StatTile label="Per lead" value={usd(data.per_lead_micros)} meta={`${num(data.volume.leads)} leads from ${num(data.volume.posts)} posts`} />
            <StatTile label="Real calls" value={num(data.real_calls)} meta={`${data.by_model.reduce((a, r) => a + r.failed, 0)} failed`} />
          </div>

          <Projector data={data} />

          <div className="two-col" style={{ marginTop: 'var(--s-6)' }}>
            <Panel>
              {data.real_calls ? (
                <BarChart title="Spend by day" subtitle="Screening every post, and extracting the leads" height={240} showTable stacked
                          format={(v) => usd(Number(v))}
                          data={data.by_day.map((d) => ({ day: dateShort(d.day), screen: Number(d.screen_micros), extract: Number(d.extract_micros) }))} xKey="day"
                          series={[{ key: 'screen', label: 'Screen' }, { key: 'extract', label: 'Extract' }]} />
              ) : (
                <><div className="viz-title">Spend by day</div><p className="muted">Appears with the first real model call.</p></>
              )}
            </Panel>
            <Panel>
              <HBars title="Spend by group" subtitle={`Last ${data.days} days`} format={(n) => usd(n)} empty="No real calls yet."
                     rows={data.by_group.filter((g) => Number(g.cost_micros) > 0).map((g) => ({ label: g.name, value: Number(g.cost_micros), note: `${g.posts} posts, ${g.leads} leads` }))} />
            </Panel>
          </div>

          <AnthropicCrossCheck />

          <Panel className="panel-flush">
            <div className="panel-header" style={{ padding: 'var(--s-5) var(--s-6) 0' }}>
              <h2 className="panel-title">By step and model</h2>
              <span className="muted">{Object.entries(data.prices).map(([m, p]) => p ? `${modelName(m)} $${p.input} in / $${p.output} out per million tokens` : '').filter(Boolean).join(' · ')}</span>
            </div>
            <DataTable columns={cols} rows={data.by_model} rowKey={(r) => `${r.stage}-${r.model}`}
                       empty={<div className="muted" style={{ padding: 'var(--s-6)' }}>No real model calls in this window.</div>} />
          </Panel>
          <div style={{ height: 'var(--s-6)' }} />
          <ShadowReport days={days} />
        </>
      )}
    </>
  )
}

type Shadow = {
  total: number; compared: number; agreed: number; failed: number; primary_leads: number; leads_agreed: number
  shadow_model: string | null; primary_model: string | null
  by_pair: { primary_decision: string; shadow_decision: string; n: number }[]
  disagreements: { post_id: string; created_at: string; primary_decision: string; primary_category: string | null; shadow_decision: string; shadow_category: string | null
    shadow_other: string | null; shadow_reason: string | null; primary_reason: string | null; text: string; group_name: string | null }[]
}
const DECISION: Record<string, string> = { lead: 'Lead', uncategorized: 'Uncategorized', not_lead: 'Not a lead' }
const pct = (n: number, d: number) => (d ? `${Math.round((n / d) * 1000) / 10}%` : 'n/a')

/**
 * Two models on the same posts: the one that decides and a shadow that does
 * not (SHADOW_SCREEN_MODEL). How often they agree, and every post where they
 * did not, to tune the cheaper one with real cases before it is trusted.
 */
function ShadowReport({ days }: { days: string }) {
  const { data } = useApi<Shadow>(`/api/costs/shadow?days=${days}`)
  if (!data) return null
  if (!data.total) {
    return <Panel><div className="panel-header"><h2 className="panel-title">Model comparison</h2></div><p className="muted" style={{ margin: 0 }}>Off. Set SHADOW_SCREEN_MODEL to screen every post with a second model beside the one that decides.</p></Panel>
  }
  return (
    <Panel>
      <div className="panel-header">
        <h2 className="panel-title">Model comparison</h2>
        <span className="muted">{modelName(data.primary_model || '')} decides · {modelName(data.shadow_model || '')} shadows</span>
      </div>
      <div className="grid-tiles" style={{ marginBottom: 'var(--s-5)' }}>
        <StatTile focal label="Agree" value={pct(data.agreed, data.compared)} meta={`${num(data.agreed)} of ${num(data.compared)} posts`} progress={data.compared ? (data.agreed / data.compared) * 100 : undefined} />
        <StatTile label="Agree on leads" value={pct(data.leads_agreed, data.primary_leads)} meta={`${num(data.leads_agreed)} of ${num(data.primary_leads)} the decider called leads`} />
        <StatTile label="Disagreements" value={num(data.compared - data.agreed)} meta={data.by_pair.map((p) => `${num(p.n)} ${DECISION[p.primary_decision]} → ${DECISION[p.shadow_decision]}`).join(', ') || 'none'} />
        <StatTile label="Shadow failed" value={num(data.failed)} meta="not counted" />
      </div>
      {data.disagreements.length === 0 ? <p className="muted">No disagreements in this window.</p> : (
        <div className="stack-tight">
          {data.disagreements.map((d) => (
            <div key={d.post_id} className="uncovered-row">
              <div className="row-tight" style={{ flexWrap: 'wrap', alignItems: 'center' }}>
                <Chip tone="pos">{modelName(data.primary_model || '')}: {DECISION[d.primary_decision]}{d.primary_category ? ` (${d.primary_category})` : ''}</Chip>
                <Chip tone="neg">{modelName(data.shadow_model || '')}: {DECISION[d.shadow_decision]}{d.shadow_category ? ` (${d.shadow_category})` : d.shadow_other ? ` (${d.shadow_other})` : ''}</Chip>
                <span className="muted">{d.group_name} · {dateShort(d.created_at)}</span>
              </div>
              <p style={{ margin: 'var(--s-2) 0 0' }}><a href={`/leads/${d.post_id}`}>{d.text || 'Text removed'}</a></p>
              {d.shadow_reason && <p className="muted" style={{ margin: 'var(--s-1) 0 0', fontSize: 'var(--t-ui)' }}>Shadow said: {d.shadow_reason}</p>}
            </div>
          ))}
        </div>
      )}
    </Panel>
  )
}

/** What a month of AI costs at a given number of groups. Every input starts at what was measured, and can be changed. */
function Projector({ data }: { data: Costs }) {
  const p = data.projection
  const [groups, setGroups] = useState(String(Math.max(data.volume.groups, 50)))
  const [perDay, setPerDay] = useState(p.posts_per_group_per_day ? p.posts_per_group_per_day.toFixed(1) : '20')
  const [leadPct, setLeadPct] = useState(p.lead_share != null ? (p.lead_share * 100).toFixed(0) : '10')

  const perGroupMonth = useMemo(() => {
    const posts = Number(perDay || 0) * 30
    return posts * p.unit.screen.micros + posts * (Number(leadPct || 0) / 100) * p.unit.extract.micros
  }, [perDay, leadPct, p.unit.screen.micros, p.unit.extract.micros])
  const month = perGroupMonth * Number(groups || 0)
  const steps = [25, 50, 100, 200]
  const estimated = p.unit.screen.source === 'estimated' || p.unit.extract.source === 'estimated'

  return (
    <Panel>
      <div className="panel-header">
        <h2 className="panel-title">Monthly AI cost at any size</h2>
        <Chip tone={estimated ? 'warn' : 'pos'} dot>{estimated ? 'Unit costs estimated' : 'Unit costs measured'}</Chip>
      </div>
      <div className="two-col" style={{ marginBottom: 0 }}>
        <div className="stack-tight">
          <div className="two-col" style={{ marginBottom: 0, gap: 'var(--s-4)' }}>
            <Field label="Groups watched" type="number" min={0} value={groups} onChange={(e) => setGroups(e.target.value)} />
            <Field label="Posts per group, per day" type="number" min={0} step="0.1" value={perDay} onChange={(e) => setPerDay(e.target.value)} />
            <Field label="Share that are leads, %" type="number" min={0} max={100} value={leadPct} onChange={(e) => setLeadPct(e.target.value)} />
          </div>
          <p className="muted" style={{ fontSize: 'var(--t-ui)', margin: 0 }}>
            Started from what arrived: {p.posts_per_group_per_day == null ? 'no posts yet' : `${p.posts_per_group_per_day.toFixed(1)} posts per group per day`}
            {p.lead_share != null ? `, ${(p.lead_share * 100).toFixed(0)}% leads${p.lead_share_from_simulated ? ' (from the demo keyword rules, not the model)' : ''}` : ''}.
          </p>
          <div className="unit-costs">
            {(['screen', 'extract'] as const).map((stage) => {
              const u = p.unit[stage]
              return (
                <div key={stage}>
                  <span className="muted">{stage === 'screen' ? 'Screening one post' : 'Extracting one lead'}</span>
                  <strong className="num">{usd(u.micros)}</strong>
                  <span className="meter-inline">{u.source === 'measured' ? `measured over ${num(u.calls)} calls` : `estimate: about ${num(u.input_tokens)} tokens in, ${num(u.output_tokens)} out${u.model ? ` on ${modelName(u.model)}` : ''}`}</span>
                </div>
              )
            })}
          </div>
        </div>
        <div>
          <div className="projector-result">
            <span className="muted">A month at {num(Number(groups || 0))} groups</span>
            <strong className="num">{usd(month)}</strong>
            <span className="muted">{usd(perGroupMonth)} per group</span>
          </div>
          <HBars format={(n) => usd(n)} rows={steps.map((g) => ({ label: `${g} groups`, value: perGroupMonth * g }))} />
        </div>
      </div>
    </Panel>
  )
}

/** Anthropic's own figure lives in SPEC's spending dashboard. An org-wide admin key never goes on a client app. */
function AnthropicCrossCheck() {
  return (
    <div style={{ margin: 'var(--s-6) 0' }}>
      <Panel>
        <div className="panel-header"><h2 className="panel-title">Check against Anthropic</h2></div>
        <p className="muted" style={{ fontSize: 'var(--t-ui)', margin: 0, lineHeight: 1.5 }}>
          Anthropic's billed cost for this install's key is in SPEC's spending dashboard, which holds the admin key. It is not read here: that key can see the whole Anthropic organization, so it does not belong on a client app. The ledger above is every call this app made.
        </p>
      </Panel>
    </div>
  )
}
